import React, { useState, useRef, useMemo } from "react";
import { Lock, Send, AtSign } from "lucide-react";
import UserAvatar from "components/UserAvatar";

function nameOf(u = {}) {
  return (
    u.name ||
    u.displayName ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email ||
    "User"
  );
}

// The @-mention query immediately before the cursor (`@` at a word boundary).
function detectMention(text, cursor) {
  const before = text.slice(0, cursor);
  const m = /(?:^|\s)@([\w.-]*)$/.exec(before);
  if (!m) return null;
  return { query: m[1], start: cursor - m[1].length - 1, end: cursor };
}

/**
 * MessageComposer (SOUND-493 + @-mentions SOUND-526).
 * -------------------------------------------------------
 * A textarea with a Reply / 🔒 Internal-note toggle (internal only) that
 * re-skins the composer amber — the internal-note safety affordance (unchanged).
 *
 * Typing `@` opens an autocomplete of internal users (`mentionUsers`); selecting
 * inserts a readable `@Name` token and TRACKS the userId. On submit the composer
 * sends the body plus an explicit `mentions: [userId]` array (the backend also
 * accepts @email parsing, but the explicit array is cleaner). Mentions whose
 * token was deleted from the body are dropped.
 *
 * `Cmd/Ctrl+Enter` sends; Enter = newline (or select the highlighted mention
 * when the autocomplete is open). Text clears only on a successful send.
 *
 * Props:
 *   - onSend(body, visibility, mentions): async; resolve on success, throw on fail
 *   - submitting, allowInternal, replyToLabel, className (as before)
 *   - mentionUsers: internal-user directory for the @-autocomplete (internal only)
 */
export default function MessageComposer({
  onSend,
  submitting = false,
  allowInternal = false,
  replyToLabel,
  className = "",
  mentionUsers = [],
}) {
  const [value, setValue] = useState("");
  // Internal note is the default (SOUND-627) so nothing is emailed to a client
  // unless someone picks Reply on purpose. Safe for clients/partners: isInternal
  // below still requires allowInternal, so they always send public.
  const [mode, setMode] = useState("internal");
  const [mentions, setMentions] = useState({}); // { userId: token }
  const [mention, setMention] = useState(null); // { query, start, end } | null
  const [activeIdx, setActiveIdx] = useState(0);
  const taRef = useRef(null);

  const isInternal = allowInternal && mode === "internal";
  const canMention = mentionUsers && mentionUsers.length > 0;

  const matches = useMemo(() => {
    if (!mention || !canMention) return [];
    const q = mention.query.toLowerCase();
    return mentionUsers
      .filter((u) => !q || nameOf(u).toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [mention, mentionUsers, canMention]);

  const publicPlaceholder = replyToLabel ? `Write a reply to ${replyToLabel}…` : "Write a reply…";
  const placeholder = isInternal
    ? "Write an internal note — only your team can see this…"
    : publicPlaceholder;

  const onChange = (e) => {
    const val = e.target.value;
    setValue(val);
    if (canMention) {
      const det = detectMention(val, e.target.selectionStart);
      setMention(det);
      setActiveIdx(0);
    }
  };

  const selectMention = (u) => {
    if (!mention) return;
    const id = u.id || u._id;
    const token = `@${nameOf(u)}`;
    const before = value.slice(0, mention.start);
    const after = value.slice(mention.end);
    const next = `${before}${token} ${after}`;
    setValue(next);
    setMentions((prev) => ({ ...prev, [id]: token }));
    setMention(null);
    const pos = (before + token + " ").length;
    setTimeout(() => {
      if (taRef.current) {
        taRef.current.focus();
        taRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const send = async () => {
    const body = value.trim();
    if (!body || submitting) return;
    // Only mentions whose token is still present in the body.
    const mentionIds = Object.entries(mentions)
      .filter(([, token]) => value.includes(token))
      .map(([id]) => id);
    try {
      await onSend(body, isInternal ? "internal" : "public", mentionIds);
      setValue("");
      setMentions({});
      setMention(null);
    } catch {
      // Draft preserved; page surfaces the failure.
    }
  };

  const onKeyDown = (e) => {
    if (mention && matches.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        selectMention(matches[Math.min(activeIdx, matches.length - 1)]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className={`relative overflow-visible rounded-lg border bg-card shadow-lg transition-colors ${
        isInternal ? "border-border border-l-2 border-l-warning" : "border-border"
      } ${className}`}
    >
      {/* Mention autocomplete — opens upward so it clears the sticky composer. */}
      {mention && matches.length > 0 && (
        <div className="absolute bottom-full left-3 right-3 z-40 mb-2 max-h-56 overflow-y-auto rounded-lg border border-border bg-card p-1 shadow-lg">
          <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <AtSign className="h-3 w-3" />
            Mention
          </div>
          {matches.map((u, i) => (
            <button
              key={u.id || u._id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                selectMention(u);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${
                i === activeIdx ? "bg-muted" : "hover:bg-muted"
              }`}
            >
              <UserAvatar user={u} />
              <span className="min-w-0 flex-1 truncate text-sm">{nameOf(u)}</span>
              <span className="truncate text-xs text-muted-foreground">{u.email}</span>
            </button>
          ))}
        </div>
      )}

      {allowInternal && (
        <div className="flex px-3 pt-3">
          <div className="inline-flex gap-1 rounded-md bg-muted p-1" role="tablist" aria-label="Reply visibility">
            <button
              type="button"
              role="tab"
              aria-selected={!isInternal}
              onClick={() => setMode("public")}
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-semibold transition-colors ${
                !isInternal ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Reply
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isInternal}
              onClick={() => setMode("internal")}
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-semibold transition-colors ${
                isInternal ? "bg-card text-warning shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Lock className="h-3 w-3" />
              Internal note
            </button>
          </div>
        </div>
      )}

      {isInternal && (
        <div className="mt-2 flex items-center gap-2 bg-warning/[0.09] px-4 py-1.5 text-xs font-semibold text-warning">
          <Lock className="h-3 w-3" />
          Internal — not visible to the client. Only your team can read this.
        </div>
      )}

      <textarea
        ref={taRef}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={3}
        className={`w-full resize-none border-0 bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 ${
          isInternal ? "bg-warning/[0.04]" : ""
        }`}
      />

      <div className="flex items-center gap-3 px-3 pb-3 pt-1">
        <span className="text-xs text-muted-foreground">
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">⌘↵</kbd> to send
          {canMention && (
            <span className="ml-2">
              · <span className="font-mono">@</span> to mention
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={send}
          disabled={submitting || !value.trim()}
          className={`btn ml-auto ${
            isInternal
              ? "bg-warning text-warning-foreground hover:brightness-95 focus-visible:ring-warning"
              : "btn-primary"
          }`}
        >
          {isInternal ? (
            <>
              <Lock className="h-4 w-4" />
              Add internal note
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Send reply
            </>
          )}
        </button>
      </div>
    </div>
  );
}
